import { useAdminSession } from '@/lib/admin-session';
import { fetchReplaySettings, fetchReplaySettingsChanges } from '@/lib/replay-settings-api';
import type { ReplaySettings, ReplaySettingsChanges } from '@matter_extractor/shared';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';

/**
 * The cache keys of Réglages > Rejeu, in ONE place: the shell (which asks
 * whether the tab exists), the page (which draws it) and the save (which
 * invalidates both) all spell them, and two spellings of a key are two caches
 * that eventually disagree about whether the replay is open.
 */
export const REPLAY_SETTINGS_KEY = 'replay-settings';
export const REPLAY_SETTINGS_CHANGES_KEY = 'replay-settings-changes';

/**
 * The texts of the instruction, or `null` when the replay is closed (§4).
 *
 * The shell and the page call this with the SAME key, so one request answers
 * both: the tab appears when the read lands, and the page it leads to is
 * already filled. `retry: false` because a 404 here is an answer, not a
 * failure to retry.
 */
export function useReplaySettings(): UseQueryResult<ReplaySettings | null> {
  const { email } = useAdminSession();
  return useQuery({
    queryKey: [REPLAY_SETTINGS_KEY, email],
    queryFn: () => fetchReplaySettings(),
    retry: false,
  });
}

/** The journal of the saves. Read by the page, once the replay is known to be open. */
export function useReplaySettingsChanges(enabled: boolean): UseQueryResult<ReplaySettingsChanges> {
  const { email } = useAdminSession();
  return useQuery({
    queryKey: [REPLAY_SETTINGS_CHANGES_KEY, email],
    queryFn: () => fetchReplaySettingsChanges(),
    retry: false,
    enabled,
  });
}
